import { BlobProvider } from "@react-pdf/renderer";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import styled from "styled-components";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background-color: #f9f9fa;
`;

const PageWrapper = styled.div`
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
`;

const ErrorMessage = styled.p`
  padding: 24px;
  text-align: center;
  color: #666;
`;

type MobileReceiptViewerProps = {
  receiptDocument: ReactElement;
};

export function MobileReceiptViewer({
  receiptDocument,
}: MobileReceiptViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState<number>();
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setPageWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: loadedPages }: { numPages: number }) => {
      setNumPages(loadedPages);
    },
    []
  );

  return (
    <Container ref={containerRef}>
      <BlobProvider document={receiptDocument}>
        {({ blob, loading, error }) => {
          if (loading || !blob) return <LoadingSpinner />;
          if (error) {
            return <ErrorMessage>Unable to display the receipt.</ErrorMessage>;
          }

          return (
            <Document
              file={blob}
              loading={<LoadingSpinner />}
              error={
                <ErrorMessage>Unable to display the receipt.</ErrorMessage>
              }
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {Array.from({ length: numPages }, (_, index) => (
                <PageWrapper key={`receipt-page-${index + 1}`}>
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </PageWrapper>
              ))}
            </Document>
          );
        }}
      </BlobProvider>
    </Container>
  );
}
